import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { ProcurementStatCard } from '@/modules/procurement/components/ProcurementStatCard';
import {
  ShoppingCart,
  TrendingUp,
  FileText,
  Truck,
  DollarSign,
  Users,
  ArrowRight,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Package,
  ClipboardList,
  RefreshCw } from
'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell } from
'recharts';

const API_URL = API_BASE;

const quickActions = [
{ name: 'New PO', href: '/procurement/purchase-orders', icon: FileText },
{ name: 'Create RFQ', href: '/procurement/rfq', icon: ClipboardList },
{ name: 'Add Vendor', href: '/procurement/vendors', icon: Users },
{ name: 'Goods Receipt', href: '/procurement/goods-receipt', icon: Package }];


const COLORS = ['#4f46e5', '#6366f1', '#818cf8', '#a5b4fc', '#c7d2fe', '#e0e7ff'];

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export const ProcurementDashboard = () => {const { t } = useTranslation("procurement");
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/procurement/dashboard`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch dashboard');
      const data = await response.json();
      setDashboardData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      setDashboardData({
        stats: {
          total_spend: 0,
          vendor_count: 0,
          pr_pending: 0,
          pr_approved: 0,
          po_draft: 0,
          po_issued: 0,
          po_in_transit: 0
        },
        recent_requisitions: [],
        recent_orders: [],
        spend_by_month: [],
        spend_by_category: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500" />
      </div>);

  }

  const stats = dashboardData?.stats || {};
  const recentPRs = dashboardData?.recent_requisitions || dashboardData?.recent_prs || [];
  const recentPOs = dashboardData?.recent_orders || dashboardData?.recent_pos || [];
  const spendByMonth = dashboardData?.spend_by_month || [];
  const spendByCategory = dashboardData?.spend_by_category || [];

  return (
    <div className="space-y-6" data-testid="procurement-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("procurementDashboard", "Procurement Dashboard")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("managePurchasingVendors", "Manage purchasing, vendors, and supply chain operations")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDashboard} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Link to="/procurement/purchase-orders">
            <Button className="gap-2 bg-indigo-600 hover:bg-indigo-700">
              <FileText className="h-4 w-4" />{t("newPurchaseOrder", "New Purchase Order")}

            </Button>
          </Link>
        </div>
      </div>

      {error &&
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <p className="text-yellow-500 text-sm">{t("usingCachedData", "Using cached data -")}{error}</p>
        </div>
      }

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <ProcurementStatCard
          title="Total Spend"
          value={stats.total_spend || 0}
          prefix="$"
          icon={DollarSign}
          color="indigo"
          trend={{ type: 'up', value: 12 }} />
        
        <ProcurementStatCard
          title="Active Vendors"
          value={stats.vendor_count ?? stats.active_vendors ?? 0}
          icon={Users}
          color="teal" />
        
        <ProcurementStatCard
          title="Pending PRs"
          value={stats.pr_pending ?? stats.pr_pending_approval ?? 0}
          icon={Clock}
          color="amber"
          subtitle="Awaiting approval" />
        
        <ProcurementStatCard
          title="Open POs"
          value={(stats.po_issued || 0) + (stats.po_in_transit || 0)}
          icon={ShoppingCart}
          color="green"
          subtitle="In progress" />
        
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-4">
        {quickActions.map((action) =>
        <Link key={action.name} to={action.href}>
            <Card className="hover:border-indigo-500 transition-colors cursor-pointer h-full">
              <CardContent className="pt-6 flex flex-col items-center text-center">
                <action.icon className="h-8 w-8 text-indigo-500 mb-3" />
                <h3 className="font-semibold">{action.name}</h3>
              </CardContent>
            </Card>
          </Link>
        )}
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Spend by Month */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">{t("monthlySpendTrend", "Monthly Spend Trend")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={spendByMonth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                  formatter={(value) => [`$${value.toLocaleString()}`, 'Spend']} />
                
                <Area
                  type="monotone"
                  dataKey="amount"
                  stroke="#4f46e5"
                  fill="#4f46e5"
                  fillOpacity={0.3} />
                
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Spend by Category */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">{t("spendByCategory", "Spend by Category")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={spendByCategory}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="category" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                  formatter={(value) => [`$${value.toLocaleString()}`, 'Spend']} />
                
                <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                  {spendByCategory.map((entry, index) =>
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  )}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Recent PRs */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">{t("recentPurchaseRequisitions", "Recent Purchase Requisitions")}</CardTitle>
            <Link to="/procurement/purchase-requisitions">
              <Button variant="ghost" size="sm" className="gap-2">{t("viewAll", "View All")}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentPRs.length === 0 ?
              <p className="text-center text-muted-foreground py-8">{t("noRecentRequisitions", "No recent requisitions")}</p> :

              recentPRs.slice(0, 5).map((pr, index) =>
              <div key={pr.pr_number || index} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                    <div>
                      <p className="font-medium">{pr.pr_number}</p>
                      <p className="text-sm text-muted-foreground">{pr.vendor_name || 'No vendor'}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium">${(pr.total_amount || 0).toLocaleString()}</span>
                      <Badge variant={pr.status === 'approved' ? 'default' : pr.status === 'pending_approval' ? 'secondary' : 'outline'}>
                        {pr.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
              )
              }
            </div>
          </CardContent>
        </Card>

        {/* Recent POs */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">{t("recentPurchaseOrders", "Recent Purchase Orders")}</CardTitle>
            <Link to="/procurement/purchase-orders">
              <Button variant="ghost" size="sm" className="gap-2">{t("viewAll", "View All")}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentPOs.length === 0 ?
              <p className="text-center text-muted-foreground py-8">{t("noRecentOrders", "No recent orders")}</p> :

              recentPOs.slice(0, 5).map((po, index) =>
              <div key={po.po_number || index} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                    <div>
                      <p className="font-medium">{po.po_number}</p>
                      <p className="text-sm text-muted-foreground">{po.vendor_name || 'No vendor'}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium">${(po.total_amount || 0).toLocaleString()}</span>
                      <Badge variant={
                  po.status === 'received' ? 'default' :
                  po.status === 'issued' ? 'secondary' :
                  'outline'
                  }>
                        {po.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
              )
              }
            </div>
          </CardContent>
        </Card>
      </div>
    </div>);

};

export default ProcurementDashboard;
